import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react'

import { cn } from '../../lib/cn'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  /** Already-translated message. Also flips the field into its invalid state. */
  error?: string | null
  hint?: string
  trailing?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, trailing, className, id, ...props },
  ref,
) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const errorId = `${inputId}-error`
  const hintId = `${inputId}-hint`

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-foreground">
        {label}
      </label>

      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          // Pointing at both keeps the hint readable once an error appears; a
          // screen reader otherwise loses the format guidance exactly when the
          // user needs it most.
          aria-describedby={cn(error && errorId, hint && hintId) || undefined}
          className={cn(
            'h-9 w-full rounded-md border bg-surface px-3 text-sm text-foreground',
            'placeholder:text-foreground-subtle transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-60',
            error ? 'border-danger' : 'border-border hover:border-border-strong',
            trailing && 'pe-10',
            className,
          )}
          {...props}
        />
        {trailing ? (
          <div className="absolute inset-y-0 end-0 flex items-center pe-1">{trailing}</div>
        ) : null}
      </div>

      {hint ? (
        <p id={hintId} className="text-xs text-foreground-subtle">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
})
